import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { KLASSEN_LABELS } from "./data";
import type { KlasseKey } from "./types";

/**
 * Klassen-Definitionsmatrix für Rahmenvertrag Sammelvertrag.
 *
 * Business-Logik: pro Klasse (1-10) werden die Deckungen ausgewählt. Zusätzlich
 * können Subklassen (1a, 1b, …) angelegt werden — pro Subklasse eine
 * Beschreibung und ihre eigenen Deckungen.
 *
 * Spalten (zweistufiger Header):
 *   KH | TK (SB 150 · 500 · 1000 · 2500) | VK/TK (12 SB-Paare)
 *
 * Modus:
 *   - "antrag"     → Checkboxes je Zelle
 *   - "beitrag"    → € Input für markierte Zellen (sonst —)
 *   - "vereinbart" → Read-only € Anzeige
 *
 * Es werden bewusst keine Fluent UI Components (Checkbox/Input) verwendet —
 * stattdessen native HTML-Elemente mit makeStyles im CI-Design.
 */
export type SammelGridSubklasse = {
  /** "a", "b", "c", … */
  letter: string;
  beschreibung: string;
  selection: Record<string, true>;
  beitraege: Record<string, string>;
};

export type SammelGridKlassenDef = {
  parent: {
    selection: Record<string, true>;
    beitraege: Record<string, string>;
  };
  subs: SammelGridSubklasse[];
};

export type SammelGridState = {
  /** Pro KFZ-Klasse die Definition. */
  klassen: Record<KlasseKey, SammelGridKlassenDef>;
  /** Versicherungssteuersatz in % (Stage 3 — gilt für alle vereinbarten Netto-Beträge). */
  versicherungssteuersatz?: string;
  /** Hochgeladenes Vereinbarungs-Dokument (Stage 3). */
  agreementFile?: { name: string; size: number; dataUrl?: string };
};

type DeckungGroup = "kh" | "tk" | "vktk";

type ColumnDef = {
  group: DeckungGroup;
  key: string;
  sb?: string;
};

const TK_SBS = ["Ohne", "150", "500", "1000", "2500"];
const VKTK_SBS = [
  "150/Ohne",
  "150/150",
  "300/Ohne",
  "300/150",
  "300/300",
  "500/Ohne",
  "500/150",
  "500/500",
  "1000/Ohne",
  "1000/150",
  "1000/1000",
  "2500/2500",
];

export const SAMMELGRID_COLUMNS: ColumnDef[] = [
  { group: "kh", key: "kh" },
  ...TK_SBS.map<ColumnDef>((sb) => ({ group: "tk", key: `tk:${sb}`, sb })),
  ...VKTK_SBS.map<ColumnDef>((sb) => ({ group: "vktk", key: `vktk:${sb}`, sb })),
];

const GROUP_LABEL: Record<DeckungGroup, string> = {
  kh: "KH",
  tk: "TK",
  vktk: "VK/TK",
};
const GROUP_LONG: Record<DeckungGroup, string> = {
  kh: "Kraftfahrzeug-Haftpflicht",
  tk: "Teilkasko",
  vktk: "Vollkasko und Teilkasko",
};

export const SAMMELGRID_KLASSEN: KlasseKey[] = [
  "pkw",
  "lieferwagen",
  "lkw",
  "zugmaschine",
  "busse",
  "anhaenger",
  "arbeitsmaschinen",
  "gabelstapler",
  "krad",
  "sonderfahrzeuge",
];

const emptyKlassenDef = (): SammelGridKlassenDef => ({
  parent: { selection: {}, beitraege: {} },
  subs: [],
});

export const initialSammelGrid = (): SammelGridState => {
  const klassen = {} as Record<KlasseKey, SammelGridKlassenDef>;
  for (const k of SAMMELGRID_KLASSEN) klassen[k] = emptyKlassenDef();
  return { klassen };
};

const nextSubLetter = (subs: SammelGridSubklasse[]): string => {
  if (subs.length === 0) return "a";
  const last = subs[subs.length - 1].letter;
  return String.fromCharCode(last.charCodeAt(0) + 1);
};

/** Selection-Anzahl über alle Klassen (Parent + Subs). */
export const countSelections = (grid: SammelGridState): number => {
  let n = 0;
  for (const k of SAMMELGRID_KLASSEN) {
    const def = grid?.klassen?.[k];
    if (!def) continue;
    n += Object.keys(def.parent.selection).length;
    for (const s of def.subs) n += Object.keys(s.selection).length;
  }
  return n;
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  tableScroll: {
    overflowX: "auto",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    backgroundColor: BRAND.white,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  // Top-level group headers (KH | TK | VK/TK)
  thGroup: {
    position: "relative",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "center",
    ...shorthands.borderRight("1px", "solid", "rgba(255,255,255,0.18)"),
  },
  thGroupShort: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  thGroupLong: {
    display: "inline-block",
    fontSize: "0.78rem",
    fontWeight: 600,
  },
  thGroupKh: { backgroundColor: "#3D8BD9" },
  thGroupTk: { backgroundColor: "#E66A2C" },
  thGroupVktk: { backgroundColor: "#2E7D4F" },
  thSb: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: BRAND.bg,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "8px",
    textAlign: "center",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("1px", "dashed", BRAND.borderDashed),
    minWidth: "84px",
    whiteSpace: "nowrap",
  },
  thSbKh: { minWidth: "300px" },
  thStub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    textAlign: "left",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRight("1px", "solid", BRAND.borderDashed),
    minWidth: "300px",
    position: "sticky",
    left: 0,
    zIndex: 3,
  },
  // Parent klasse row — neutrales Grau zum Hervorheben (kein Lila).
  parentRow: {
    backgroundColor: "#f1f1f3",
  },
  parentLabelCell: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontWeight: 700,
    backgroundColor: "#f1f1f3",
    position: "sticky",
    left: 0,
    zIndex: 2,
    ...shorthands.borderTop("1px", "solid", "#c9c5cf"),
    ...shorthands.borderRight("1px", "solid", "#c9c5cf"),
  },
  parentLabelInner: {
    display: "inline-flex",
    alignItems: "baseline",
    columnGap: "8px",
  },
  parentNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
    height: "24px",
    paddingLeft: "6px",
    paddingRight: "6px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 800,
    ...shorthands.borderRadius("999px"),
  },
  // Sub klasse row
  subLabelCell: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
    ...shorthands.borderRight("1px", "solid", "#c9c5cf"),
    position: "sticky",
    left: 0,
    zIndex: 2,
  },
  subLabelInner: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  subTag: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.92rem",
    color: BRAND.ink,
    fontWeight: 700,
    minWidth: "34px",
  },
  beschreibungInput: {
    flex: 1,
    minWidth: "160px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
    boxSizing: "border-box",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  beschreibungReadonly: {
    flex: 1,
    fontSize: "0.88rem",
    color: BRAND.ink,
    fontStyle: "italic",
  },
  cell: {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "4px",
    paddingRight: "4px",
    textAlign: "center",
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
    ...shorthands.borderLeft("1px", "solid", "#dcdce0"),
    verticalAlign: "middle",
  },
  cellParent: {
    backgroundColor: "#f1f1f3",
    ...shorthands.borderTop("1px", "solid", "#c9c5cf"),
    ...shorthands.borderLeft("1px", "solid", "#dcdce0"),
  },
  // native checkbox — blau im CI für die Markierungen.
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: "#3D8BD9",
    margin: 0,
  },
  // beitrag input
  beitragInput: {
    width: "82px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.ink,
    textAlign: "right",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "6px",
    paddingRight: "6px",
    boxSizing: "border-box",
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  beitragMuted: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
  beitragValue: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  // Add-Sub button row
  addRow: {
    backgroundColor: BRAND.white,
  },
  addCell: {
    paddingTop: "8px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
    ...shorthands.borderBottom("1px", "solid", "#dcdce0"),
  },
  // Sticky wrapper inside the colSpan'd add-row td → button bleibt beim
  // horizontalen Scrollen am linken Rand sichtbar.
  addInner: {
    position: "sticky",
    left: "14px",
    display: "inline-block",
  },
  addBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "dashed", "#9b96a3"),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "background-color, color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "#f1f1f3",
      ...shorthands.borderColor(BRAND.ink),
    },
  },
});

export type SammelvertragGridMode = "antrag" | "beitrag" | "vereinbart";

type Props = {
  grid: SammelGridState;
  onChange: (next: SammelGridState) => void;
  mode?: SammelvertragGridMode;
};

const safeGrid = (g: SammelGridState | undefined): SammelGridState => {
  const klassen = {} as Record<KlasseKey, SammelGridKlassenDef>;
  for (const k of SAMMELGRID_KLASSEN) {
    const incoming = g?.klassen?.[k];
    if (
      incoming &&
      typeof incoming === "object" &&
      "parent" in incoming &&
      Array.isArray(incoming.subs)
    ) {
      klassen[k] = {
        parent: {
          selection: (incoming.parent?.selection ?? {}) as Record<string, true>,
          beitraege: (incoming.parent?.beitraege ?? {}) as Record<string, string>,
        },
        subs: incoming.subs.map((s) => ({
          letter: s.letter ?? "a",
          beschreibung: s.beschreibung ?? "",
          selection: (s.selection ?? {}) as Record<string, true>,
          beitraege: (s.beitraege ?? {}) as Record<string, string>,
        })),
      };
    } else {
      klassen[k] = emptyKlassenDef();
    }
  }
  return { klassen, agreementFile: g?.agreementFile };
};

const SammelvertragGrid = ({ grid: rawGrid, onChange, mode = "antrag" }: Props) => {
  const styles = useStyles();
  const grid = safeGrid(rawGrid);
  // Struktur (Subklassen + Beschreibung + Selektion) wird im Antrag-Schritt
  // festgelegt. Ab "beitrag" sind nur noch die € Eingaben editierbar.
  const structureLocked = mode !== "antrag";

  const updateKlasse = (
    klasse: KlasseKey,
    patch: (def: SammelGridKlassenDef) => SammelGridKlassenDef,
  ) => {
    const cur = grid.klassen[klasse] ?? emptyKlassenDef();
    onChange({
      ...grid,
      klassen: { ...grid.klassen, [klasse]: patch(cur) },
    });
  };

  const toggleParentCell = (klasse: KlasseKey, colKey: string) => {
    updateKlasse(klasse, (def) => {
      const next = { ...def.parent.selection };
      if (next[colKey]) delete next[colKey];
      else next[colKey] = true;
      return { ...def, parent: { ...def.parent, selection: next } };
    });
  };
  const setParentBeitrag = (klasse: KlasseKey, colKey: string, value: string) => {
    updateKlasse(klasse, (def) => {
      const next = { ...def.parent.beitraege };
      if (!value) delete next[colKey];
      else next[colKey] = value;
      return { ...def, parent: { ...def.parent, beitraege: next } };
    });
  };
  const toggleSubCell = (klasse: KlasseKey, subIdx: number, colKey: string) => {
    updateKlasse(klasse, (def) => {
      const subs = def.subs.map((s, i) => {
        if (i !== subIdx) return s;
        const sel = { ...s.selection };
        if (sel[colKey]) delete sel[colKey];
        else sel[colKey] = true;
        return { ...s, selection: sel };
      });
      return { ...def, subs };
    });
  };
  const setSubBeitrag = (klasse: KlasseKey, subIdx: number, colKey: string, value: string) => {
    updateKlasse(klasse, (def) => {
      const subs = def.subs.map((s, i) => {
        if (i !== subIdx) return s;
        const bs = { ...s.beitraege };
        if (!value) delete bs[colKey];
        else bs[colKey] = value;
        return { ...s, beitraege: bs };
      });
      return { ...def, subs };
    });
  };
  const setSubBeschreibung = (klasse: KlasseKey, subIdx: number, value: string) => {
    updateKlasse(klasse, (def) => {
      const subs = def.subs.map((s, i) =>
        i === subIdx ? { ...s, beschreibung: value } : s,
      );
      return { ...def, subs };
    });
  };
  const addSub = (klasse: KlasseKey) => {
    updateKlasse(klasse, (def) => ({
      ...def,
      subs: [
        ...def.subs,
        {
          letter: nextSubLetter(def.subs),
          beschreibung: "",
          selection: {},
          beitraege: {},
        },
      ],
    }));
  };

  const renderCell = (
    isSelected: boolean,
    onToggle: () => void,
    beitragValue: string,
    onBeitrag: (v: string) => void,
  ) => {
    if (mode === "antrag") {
      return (
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={isSelected}
          onChange={onToggle}
        />
      );
    }
    // "beitrag" und "vereinbart" → editierbares € Input für markierte Zellen
    if (!isSelected) return <span className={styles.beitragMuted}>—</span>;
    return (
      <input
        type="text"
        className={styles.beitragInput}
        placeholder="0,00 €"
        value={beitragValue}
        onChange={(e) => onBeitrag(e.target.value)}
      />
    );
  };

  const groupCounts: Record<DeckungGroup, number> = {
    kh: SAMMELGRID_COLUMNS.filter((c) => c.group === "kh").length,
    tk: SAMMELGRID_COLUMNS.filter((c) => c.group === "tk").length,
    vktk: SAMMELGRID_COLUMNS.filter((c) => c.group === "vktk").length,
  };
  const totalCols = SAMMELGRID_COLUMNS.length + 1;

  return (
    <div className={styles.root}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thStub} rowSpan={2}>
                Klasse / Beschreibung
              </th>
              <th
                className={mergeClasses(styles.thGroup, styles.thGroupKh)}
                colSpan={groupCounts.kh}
                title={GROUP_LONG.kh}
              >
                <span className={styles.thGroupShort}>{GROUP_LABEL.kh}</span>
                <span className={styles.thGroupLong}>{GROUP_LONG.kh}</span>
              </th>
              <th
                className={mergeClasses(styles.thGroup, styles.thGroupTk)}
                colSpan={groupCounts.tk}
                title={GROUP_LONG.tk}
              >
                <span className={styles.thGroupShort}>{GROUP_LABEL.tk}</span>
                <span className={styles.thGroupLong}>{GROUP_LONG.tk}</span>
              </th>
              <th
                className={mergeClasses(styles.thGroup, styles.thGroupVktk)}
                colSpan={groupCounts.vktk}
                title={GROUP_LONG.vktk}
              >
                <span className={styles.thGroupShort}>{GROUP_LABEL.vktk}</span>
                <span className={styles.thGroupLong}>{GROUP_LONG.vktk}</span>
              </th>
            </tr>
            <tr>
              {SAMMELGRID_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={mergeClasses(styles.thSb, (col.group === "kh") && styles.thSbKh)}
                  title={GROUP_LONG[col.group]}
                >
                  {col.sb ?? GROUP_LABEL[col.group]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SAMMELGRID_KLASSEN.map((klasse, idx) => {
              const def = grid.klassen[klasse];
              const num = idx + 1;
              return (
                <KlasseBlock
                  key={klasse}
                  klasse={klasse}
                  num={num}
                  def={def}
                  mode={mode}
                  structureLocked={structureLocked}
                  totalCols={totalCols}
                  renderCell={renderCell}
                  onToggleParent={(col) => toggleParentCell(klasse, col)}
                  onSetParentBeitrag={(col, v) => setParentBeitrag(klasse, col, v)}
                  onToggleSub={(subIdx, col) => toggleSubCell(klasse, subIdx, col)}
                  onSetSubBeitrag={(subIdx, col, v) =>
                    setSubBeitrag(klasse, subIdx, col, v)
                  }
                  onSetSubBeschreibung={(subIdx, v) =>
                    setSubBeschreibung(klasse, subIdx, v)
                  }
                  onAddSub={() => addSub(klasse)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

type KlasseBlockProps = {
  klasse: KlasseKey;
  num: number;
  def: SammelGridKlassenDef;
  mode: SammelvertragGridMode;
  structureLocked: boolean;
  totalCols: number;
  renderCell: (
    isSelected: boolean,
    onToggle: () => void,
    beitragValue: string,
    onBeitrag: (v: string) => void,
  ) => React.ReactNode;
  onToggleParent: (colKey: string) => void;
  onSetParentBeitrag: (colKey: string, value: string) => void;
  onToggleSub: (subIdx: number, colKey: string) => void;
  onSetSubBeitrag: (subIdx: number, colKey: string, value: string) => void;
  onSetSubBeschreibung: (subIdx: number, value: string) => void;
  onAddSub: () => void;
};

const KlasseBlock = ({
  klasse,
  num,
  def,
  structureLocked,
  totalCols,
  renderCell,
  onToggleParent,
  onSetParentBeitrag,
  onToggleSub,
  onSetSubBeitrag,
  onSetSubBeschreibung,
  onAddSub,
}: KlasseBlockProps) => {
  const styles = useStyles();
  return (
    <>
      <tr className={styles.parentRow}>
        <td className={styles.parentLabelCell}>
          <span className={styles.parentLabelInner}>
            <span className={styles.parentNumber}>{num}</span>
            <span>{KLASSEN_LABELS[klasse]}</span>
          </span>
        </td>
        {SAMMELGRID_COLUMNS.map((col) => (
          <td key={col.key} className={mergeClasses(styles.cell, styles.cellParent)}>
            {renderCell(
              !!def.parent.selection[col.key],
              () => onToggleParent(col.key),
              def.parent.beitraege[col.key] ?? "",
              (v) => onSetParentBeitrag(col.key, v),
            )}
          </td>
        ))}
      </tr>
      {def.subs.map((sub, subIdx) => (
        <tr key={`${klasse}-${sub.letter}`}>
          <td className={styles.subLabelCell}>
            <div className={styles.subLabelInner}>
              <span className={styles.subTag}>
                {num}
                {sub.letter})
              </span>
              {structureLocked ? (
                <span className={styles.beschreibungReadonly}>
                  {sub.beschreibung || "—"}
                </span>
              ) : (
                <input
                  type="text"
                  className={styles.beschreibungInput}
                  placeholder="Beschreibung der Subklasse"
                  value={sub.beschreibung}
                  onChange={(e) => onSetSubBeschreibung(subIdx, e.target.value)}
                />
              )}
            </div>
          </td>
          {SAMMELGRID_COLUMNS.map((col) => (
            <td key={col.key} className={styles.cell}>
              {renderCell(
                !!sub.selection[col.key],
                () => onToggleSub(subIdx, col.key),
                sub.beitraege[col.key] ?? "",
                (v) => onSetSubBeitrag(subIdx, col.key, v),
              )}
            </td>
          ))}
        </tr>
      ))}
      {!structureLocked && (
        <tr className={styles.addRow}>
          <td className={styles.addCell} colSpan={totalCols}>
            <span className={styles.addInner}>
              <button type="button" className={styles.addBtn} onClick={onAddSub}>
                + Klasse hinzufügen
              </button>
            </span>
          </td>
        </tr>
      )}
    </>
  );
};

export default SammelvertragGrid;
